"use client";
// Modal — hộp thoại CUỘN ĐƯỢC trên mobile (overlay cuộn, panel max-h, header sticky,
// thân overscroll-contain), đóng bằng Esc + click nền, khóa cuộn nền khi mở.
// Render qua React Portal ra document.body → THOÁT khỏi cha có transform/overflow
// (vd trang bọc trong .animate-fade-in dùng translateY làm `position:fixed` bị "nhốt"
// trong ô của trang → hộp lệch khỏi vùng nhìn khi NV ở đầu/cuối trang). createPortal
// đưa overlay thẳng ra body nên hộp luôn căn giữa MÀN HÌNH, hiện đầy đủ.
// ConfirmProvider/useConfirm — thay window.confirm bằng hộp đẹp trả Promise<boolean>.
import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui";

type ModalSize = "sm" | "md" | "lg";

const SIZE: Record<ModalSize, string> = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
};

// ── LỚP TRÊN CÙNG (hộp-trong-hộp) ────────────────────────────────────────────
// Mỗi hộp đang mở đẩy một thẻ vào ngăn xếp này; CHỈ hộp ở ĐỈNH mới xử phím (Esc + khoá Tab).
//
// 🩸 Đo 16/09/2026 tại `/inventory/counts?them=1`: hộp xác nhận "Tạo phiên kiểm kê?" mở TRONG hộp
//    thêm-nhanh, một cú Esc chạy `onClose` của CẢ HAI — hai listener cùng nằm trên `document`, không
//    cái nào biết cái kia. Người dùng bấm Esc để rút khỏi câu hỏi thì bị ném ngay vào câu hỏi thứ
//    hai ("Đóng và bỏ những gì đang nhập?"); ở thứ tự listener ngược lại thì `confirm()` của hộp
//    ngoài đè state của `ConfirmProvider` ⇒ lời hứa của hộp trong không bao giờ resolve.
//    `stopPropagation` không vá được: chặn lan truyền không chặn được listener anh em cùng một nút.
//    Chi tiết: docs/feature/hop-thoai-chong-lop.md
const nganXep: string[] = [];
const oDinh = (the: string) => nganXep[nganXep.length - 1] === the;

// ── KHOÁ CUỘN NỀN — ĐẾM THEO NGĂN XẾP, KHÔNG THEO TỪNG HỘP ───────────────────
//
// 🩸 Đo 17/09/2026 (audit sau lượt A — docs/feature/hop-thoai-chong-lop.md hộp ⛳ 17/09):
//    bản trước để MỖI hộp tự nhớ `prev = body.style.overflow` lúc mở và tự trả lại lúc cleanup,
//    độc lập nhau. Kịch bản dựng lại được: hộp thêm-nhanh mở (prev="") → gõ → Esc → hộp xác nhận
//    mở (prev="hidden", vì hộp ngoài đang khoá) → bấm **Back** ⇒ `?them=1` mất ⇒ hộp NGOÀI đóng,
//    trả `""`; hộp xác nhận VẪN mở (nó là anh em dưới `ConfirmProvider` ở components/Providers.tsx,
//    không phụ thuộc hộp ngoài) → bấm "Ở lại nhập tiếp" ⇒ cleanup của nó trả lại **"hidden"**
//    ⇒ CẢ APP không cuộn được tới khi tải lại trang. Hai hộp đóng KHÔNG theo thứ tự mở thì "trả lại
//    giá trị tôi thấy lúc tôi mở" là một lời hứa sai.
//
// 🔑 Ngăn xếp ở trên ĐÃ trả lời được câu duy nhất cần hỏi — "còn hộp nào đang mở không" — chỉ là
//    trước nay khoá cuộn không hỏi nó. Nay: khoá ở lượt 0→1, mở ở lượt 1→0, giữa chừng không đụng.
//    StrictMode gọi hiệu ứng hai lần cũng không sai: cặp push/lock và splice/unlock luôn đi đôi.
let cuonGoc: string | null = null;
function khoaCuon() {
  if (nganXep.length !== 1) return; // đã có hộp khác khoá rồi — đừng ghi đè "hidden" lên `cuonGoc`
  cuonGoc = document.body.style.overflow;
  document.body.style.overflow = "hidden";
}
function moCuon() {
  if (nganXep.length !== 0) return; // còn hộp đang mở ⇒ nền vẫn phải khoá
  document.body.style.overflow = cuonGoc ?? "";
  cuonGoc = null;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = "md",
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: ModalSize;
}) {
  // Chỉ render Portal sau khi mounted ở client (tránh lệch SSR/hydrate).
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    setMounted(true);
  }, []);

  // Hộp (panel) — để khóa tiêu điểm (focus-trap) trong hộp.
  const panelRef = React.useRef<HTMLDivElement>(null);

  // Thẻ của hộp này trong ngăn xếp. Hiệu ứng đẩy/gỡ chỉ phụ thuộc `open` — KHÔNG kèm `onClose`: nơi
  // gọi dựng lại `onClose` khá thường (`ThemNhanhModal` đổi nó mỗi khi người dùng gõ chữ đầu tiên),
  // mà chạy lại hiệu ứng là hộp này nhảy lên đỉnh trong lúc một hộp khác đang nằm trên nó.
  const the = React.useId();
  React.useEffect(() => {
    if (!open) return;
    nganXep.push(the);
    khoaCuon();
    return () => {
      const i = nganXep.lastIndexOf(the);
      if (i !== -1) nganXep.splice(i, 1);
      moCuon();
    };
  }, [open, the]);

  // Esc đóng. (Khoá cuộn nền ĐÃ CHUYỂN lên hiệu ứng ngăn xếp ở trên — 17/09/2026: nó phải đếm theo
  // số hộp đang mở, mà hiệu ứng này chạy lại mỗi khi `onClose` đổi danh tính.)
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && oDinh(the)) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, the]);

  // ── KHÓA TIÊU ĐIỂM (focus-trap, a11y) ──────────────────────────────────────
  // Khi mở: nhớ phần tử đang focus → đưa focus vào hộp; giữ Tab/Shift+Tab QUẨN
  // TRONG hộp (không nhảy ra nền sau lưng); khi đóng: TRẢ focus về chỗ cũ (nút
  // đã mở hộp) để người dùng bàn phím không "mất chỗ".
  React.useEffect(() => {
    if (!open || !mounted) return;
    const prevFocused = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const SELECTOR =
      'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';
    const focusables = () =>
      panel
        ? Array.from(panel.querySelectorAll<HTMLElement>(SELECTOR)).filter(
            (el) => el.offsetParent !== null,
          )
        : [];
    // Đưa focus vào phần tử focus được đầu tiên, hoặc chính hộp.
    (focusables()[0] ?? panel)?.focus();

    const onKey = (e: KeyboardEvent) => {
      // Hộp nằm DƯỚI một hộp khác thì nhường luôn phím Tab — hai bẫy tiêu điểm cùng nghe trên
      // `document` sẽ kéo tiêu điểm về hai hộp khác nhau trong cùng một cú gõ.
      if (e.key !== "Tab" || !panel || !oDinh(the)) return;
      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      prevFocused?.focus?.();
    };
  }, [open, mounted, the]);

  if (!open || !mounted || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[90] overflow-y-auto bg-black/40 p-4 backdrop-blur-[2px]"
      onClick={onClose}
      role="presentation"
    >
      <div className="flex min-h-full items-center justify-center">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "outline-none",
            "flex max-h-[calc(100dvh-2rem)] w-full flex-col overflow-hidden rounded-xl border bg-card shadow-elevate",
            "animate-fade-in motion-reduce:animate-none",
            SIZE[size],
          )}
        >
          {/* Đầu đề sticky */}
          <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b bg-card px-5 py-4">
            <h3 className="min-w-0 truncate text-base font-semibold">{title}</h3>
            <button
              type="button"
              aria-label="Đóng"
              onClick={onClose}
              className="-mr-1.5 grid h-9 w-9 shrink-0 place-items-center rounded-md text-muted-fg transition hover:bg-muted hover:text-fg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {/* Thân cuộn được */}
          <div className="overflow-y-auto overscroll-contain px-5 py-4 text-sm">{children}</div>
          {/* Chân (tùy chọn) */}
          {footer && (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t bg-card px-5 py-3">{footer}</div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ---------------- Confirm (thay window.confirm) ---------------- */

type ConfirmOptions = {
  title: string;
  message?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  tone?: "default" | "danger";
};

type ConfirmState = ConfirmOptions & { resolve: (ok: boolean) => void };

const ConfirmCtx = React.createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<ConfirmState | null>(null);

  /**
   * Yêu cầu ĐANG TREO — giữ ở `ref`, không đọc qua `state`.
   *
   * 🩸 Đo 17/09/2026 (audit sau lượt A): bản trước làm `setState({ ...opts, resolve })` **KHÔNG
   *    ĐIỀU KIỆN**. Gọi `confirm()` lần thứ hai trong lúc hộp thứ nhất còn mở là **thay** `resolve`
   *    thứ nhất bằng cái mới ⇒ `await confirm()` thứ nhất **không bao giờ settle**. Form nào giữ
   *    `pending` xuyên qua nó (`app/inventory/issue/IssueForm.tsx`) thì nút kẹt vĩnh viễn —
   *    không lỗi, không log, chỉ một nút chết. Lượt `3a3baa17` (ngăn xếp phím) đóng được **một**
   *    lối vào (cú Esc rò xuống hộp dưới), không đóng được cơ chế.
   *
   * 🔑 `ref` chứ không phải `state`: `confirm()` nằm trong `useCallback` không phụ thuộc gì (mọi
   *    nơi gọi giữ một tham chiếu ổn định) nên nó KHÔNG đọc được `state` của lượt render mới nhất.
   *    Và updater của `setState` phải THUẦN — StrictMode gọi nó hai lần, resolve trong đó là sai
   *    nếp dù `resolve` có idempotent.
   */
  const dangCho = React.useRef<ConfirmState | null>(null);

  const confirm = React.useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        // Yêu cầu CŨ bị chen ngang thì phải được TRẢ LỜI, không được bỏ treo. Trả `false` = nghĩa
        // an toàn sẵn có của mọi nơi gọi (`if (!ok) return`) ⇒ không nơi gọi nào phải sửa.
        // ⚖️ Đã cân và BỎ hàng đợi FIFO: câu hỏi thứ hai gần như luôn là một nhánh KHÁC của cùng
        //    thao tác, xếp hàng là bày ra câu hỏi lạc ngữ cảnh sau khi người dùng đã đi tiếp.
        //    Giữ nguyên chốt §5 của docs/feature/hop-thoai-chong-lop.md: không dựng ngăn xếp câu hỏi.
        const truoc = dangCho.current;
        const moi: ConfirmState = { ...opts, resolve };
        dangCho.current = moi;
        setState(moi);
        truoc?.resolve(false);
      }),
    [],
  );

  const finish = (ok: boolean) => {
    const s = dangCho.current;
    dangCho.current = null;
    setState(null);
    s?.resolve(ok);
  };

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <Modal
        open={state !== null}
        onClose={() => finish(false)}
        title={state?.title ?? ""}
        size="sm"
        footer={
          <>
            <Button variant="outline" size="md" onClick={() => finish(false)}>
              {state?.cancelText ?? "Hủy bỏ"}
            </Button>
            <Button
              variant={state?.tone === "danger" ? "destructive" : "default"}
              size="md"
              onClick={() => finish(true)}
            >
              {state?.confirmText ?? "Đồng ý"}
            </Button>
          </>
        }
      >
        <div className="text-sm text-muted-fg">{state?.message}</div>
      </Modal>
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = React.useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm phải dùng bên trong <ConfirmProvider>");
  return ctx;
}
